
import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AdvisorySessionForm } from '@/components/advisory/AdvisorySessionForm';
import { Link } from 'react-router-dom';

const AdvisoryHero = () => {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleWebhookSubmit = async (formData: Record<string, any>) => {
    try {
      const webhookUrl = "https://hook.us2.make.com/f9waudrud3pvymdixk0frbpq9t90bdna";
      
      await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify({
          ...formData,
          source: "Advisory Hero Form",
          timestamp: new Date().toISOString(),
          page: window.location.href
        }),
      });

      toast({
        title: "Thank you!",
        description: "Your request has been submitted. We'll contact you shortly.",
      });
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error triggering webhook:", error);
      toast({
        title: "Submission failed",
        description: "There was an issue processing your request. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/lovable-uploads/Legal_Conference_Call_Zoom.jpg"
          alt="Legal Conference Call"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 to-background/70"></div>
      </div>
      
      <div className="container mx-auto relative z-10 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            AI <span className="text-gradient">Advisory Services</span> for Legal Teams
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            Expert guidance to navigate the evolving landscape of AI in legal practice.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={() => setIsDialogOpen(true)}
            >
              Book Free Advisory Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Link to="/services">
              <Button variant="outline" size="lg">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Advisory Session Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Your Free Advisory Session</DialogTitle>
            <DialogDescription>
              Fill out the form below to schedule your complimentary advisory session with our legal AI experts.
            </DialogDescription>
          </DialogHeader>
          <AdvisorySessionForm onSubmit={handleWebhookSubmit} />
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default AdvisoryHero;
